/*
 *  Copyright (c) 2003-2019 GameDuell GmbH, All Rights Reserved
 *  This document is strictly confidential and sole property of GameDuell GmbH, Berlin, Germany
 */
import { User } from '@gd/storage'
import { Zone } from '@/model/Zone'
import { Tournament } from '@/model/Tournament'
import { ZoneSelection } from '@/model/ZoneSelection'
import { Balance } from '@/model/UserData'
import { TournamentHistoryState } from './modules/history'
import { ResultState } from './modules/results'
import { JoinState } from './modules/join'
import { MatchingState } from '@/model/Matching'
import { RankingState } from './modules/ranking'
import { WinningDistributionState } from './modules/winningDistribution'

/**
 * state: the core of the vuex store. It contains all relevant data which are needed for the UI
 *
 * Recommended to read before using: https://vuex.vuejs.org/guide/state.html
 */
export interface StoreState {
  announcement?: string
  user: User
  balance: Balance
  zoneTeasers: Array<ZoneSelection>
  zone: Zone
  tournament: Tournament
  error?: TError
  gameRulesLoaded: boolean
}

export interface State extends StoreState {
  history: TournamentHistoryState
  results: ResultState
  result: JoinState
  matchingData: MatchingState
  ranking: RankingState
  join: JoinState
  winningDistribution: WinningDistributionState
}

/**
 * Put everything in what makes sense to track via jslog int the error object.
 * Theoretically we don't need this object. But it makes it easier to work with typescript.
 *
 * Note the TError object is only allowed to contain simple type properties like string, boolean, number.
 *
 * TError: TournamentErrors
 */
export interface TError {
  id?: string
  responseStatus?: number
}
/**
 * all states should be initialized with a default which should not brake the UI.
 *
 * > As specailly if you have deep object access don't let them empty. E.g.:
 * > Given is following object:
 * >    bananas.foo.id
 * > Don't initialize it in default with
 * >    "banana: {}"
 * > ui access on banana.foo.id will result in a exception. Better do:
 * >    "banana: {foo: ''}"
 */
const state: StoreState = {
  announcement: null,
  user: {
    actorType: '',
    currency: '',
    id: '',
    locale: '',
    loyalty: '',
    name: '',
    payout: undefined,
    forcePayout: undefined,
    bonusVisualization: undefined,
    boku: false,
    avatarUrl: '',
  },
  error: {},
  balance: {
    balance: '',
    bonus: '',
    gdCurrency: '',
    withdrawable: '',
  },
  zoneTeasers: [],
  // active selected zone
  zone: {
    zoneId: '',
    gametype: '',
    tournaments: [],
  },
  // active selected tournament
  tournament: createEmptyTournament(),
  gameRulesLoaded: false,
}

/**
 * Create bare model for type Tournament
 */
function createEmptyTournament(): Tournament {
  return {
    tournamentId: '',
    name: '',
    // e.g.: 'speed'
    gameVariant: '',
    tournamentRank: 0,
    tournamentScore: 0,
    games: [],
    gametype: '',
    joined: false,
    dates: {
      competitionEnd: '',
      competitionStart: '',
      registrationEnd: '',
      registrationStart: '',
      lobbyEnd: '',
      lobbyStart: '',
      secondsUntilLobbyStart: -1,
      secondsUntilLobbyEnd: -1,
    },
    metaData: {
      distributionSchema: '',
      entryFee: '',
      numberOfGames: 0,
      numberOfTickets: 0,
      playersPerGame: 0,
      restartFee: '',
      roundsPerGame: 0,
      totalWin: '',
      gameTechs: [],
      image: '',
      rejoinsUnlimited: true,
      rejoinsRemaining: 1,
      clearancePhase: false,
    },
  }
}

export { state, createEmptyTournament }
